import { uploadHint, PHOTO_ACCEPT } from '@theme/pb-upload-policy';
/**
 * PB-FND-75 — Upload field plugin (registry adapter).
 * Extracted from pb-field-runtime; wired via createUploadAdapter(deps).
 */

/**
 * @param {object} deps — shared field-runtime helpers
 */
export function createUploadAdapter(deps) {
  const {
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    resolveRuntimeParseSettings,
    renderControlShell,
    renderImageColumn,
    delegateInitStep,
    bindUploadWrapInteractions,
    uploadAcceptAttr,
  } = deps;

  return {
    id: 'upload',
    matches(field) {
      const t = String((field && field.type) || '');
      return t.includes('Upload') && !fieldTypeLower(field).includes('ai image');
    },
    renderStep(ctx) {
      const field = ctx.field;
      const esc = resolveEsc(ctx);
      const settingsRaw = field.settings;
      const settingsStr = settingsToString(settingsRaw);
      const parseFn = resolveRuntimeParseSettings(ctx);
      const uploadSettings = parseFn(settingsRaw);
      const uploadRequired = !!(uploadSettings && uploadSettings.required);
      const reqAttr = uploadRequired ? 'true' : 'false';
      const existingUploadVal = resolveSelections(ctx)[field.label];
      const uploadValArr = Array.isArray(existingUploadVal)
        ? existingUploadVal
        : existingUploadVal
          ? [existingUploadVal]
          : [];
      const initialVal = JSON.stringify(uploadValArr);
      const hasUploadVal = uploadValArr.length > 0;
      const acceptAttr = uploadAcceptAttr(uploadSettings).replace('image/*', PHOTO_ACCEPT);
      const innerHtml =
        '<div class="pb-upload-wrap pb-upload-inner" data-label="' +
        esc(field.label) +
        '" data-settings="' +
        esc(settingsStr) +
        '">' +
        '<input type="hidden" class="builder-required" data-label="' +
        esc(field.label) +
        '" data-required="' +
        reqAttr +
        '" data-valid="' +
        (hasUploadVal ? 'true' : 'false') +
        '" value="' +
        esc(initialVal) +
        '">' +
        '<input type="file" class="pb-upload-file-input" style="display:none;" accept="' +
        esc(acceptAttr) +
        '" multiple>' +
        '<div class="pb-upload-row">' +
        '<div class="pb-upload-preview-list"></div>' +
        '<button type="button" class="pb-upload-empty">+</button>' +
        '</div>' +
        '<p class="pb-upload-hint">' + esc(uploadHint(uploadSettings)) + '</p>' +
        '<div class="pb-upload-message" role="status" aria-live="polite" style="display:none;"></div>' +
        '</div>';
      const visRaw = String(field.visual || '').trim();
      return {
        controlHtml: renderControlShell(field, innerHtml, ctx, { useQuestionHighlight: !visRaw }),
        imageHtml: renderImageColumn(field, ctx),
        stepAttrs: '',
        extraClasses: !visRaw ? 'pb-step-hide-question' : '',
      };
    },
    initStep(stepEl) {
      delegateInitStep(stepEl, '.pb-upload-wrap', function (wrap) {
        if (wrap.closest('.pb-ai-image-wrap')) return;
        bindUploadWrapInteractions(wrap);
        if (typeof window === 'undefined' || !window.productBuilder) return;
        if (window.productBuilder.renderUploadPreviews) {
          const urls = window.productBuilder.getUploadUrls
            ? window.productBuilder.getUploadUrls(wrap)
            : [];
          window.productBuilder.renderUploadPreviews(wrap, urls || []);
        }
      });
    },
    validateStep(stepEl) {
      const wrap = stepEl && stepEl.querySelector('.pb-upload-wrap:not(.pb-ai-image-wrap .pb-upload-wrap)');
      if (!wrap || wrap.closest('.pb-ai-image-wrap')) return true;
      const hidden = wrap.querySelector('input.builder-required[data-label]');
      if (!hidden || hidden.dataset.required !== 'true') return true;
      return hidden.dataset.valid === 'true';
    },
    hydrateStep(stepEl) {
      delegateInitStep(stepEl, '.pb-upload-wrap', function (wrap) {
        if (wrap.closest('.pb-ai-image-wrap')) return;
        bindUploadWrapInteractions(wrap);
        if (typeof window === 'undefined' || !window.productBuilder?.renderUploadPreviews) return;
        const urls = window.productBuilder.getUploadUrls
          ? window.productBuilder.getUploadUrls(wrap)
          : [];
        window.productBuilder.renderUploadPreviews(wrap, urls || []);
      });
    },
    flushStep(stepEl, ctx) {
      const wrap = stepEl && stepEl.querySelector('.pb-upload-wrap');
      if (!wrap || wrap.closest('.pb-ai-image-wrap') || !ctx.selections) return;
      if (typeof window !== 'undefined' && window.productBuilder?.getUploadUrls) {
        ctx.selections[ctx.field.label] = window.productBuilder.getUploadUrls(wrap);
      }
    },
  };
}
